/**
 * Reusable color swatch primitives for species, operation, and assortment legends.
 *
 * Design contract (scszq.4): square swatches, NOT rounded text pills.
 * Use these everywhere a small colored identity marker appears in the UI.
 */
import React from 'react';

// ColorSwatch — small square colored block

export interface ColorSwatchProps {
  color: string;
  /** Side length in px. Default: 10 */
  size?: number;
  /** Additional inline styles merged with the swatch element. */
  /**
   * @internal DS-internal style prop; forbidden in route-owned code.
   */
  style?: React.CSSProperties;
  /** CSS class name applied to the swatch element. */
  className?: string;
  /** data-testid passed through for test assertions. */
  'data-testid'?: string;
}

/**
 * A small square color swatch. Use inside flex containers to visually identify
 * species, operation types, or assortments.
 */
export const ColorSwatch: React.FC<ColorSwatchProps> = ({
  color,
  size = 10,
  style,
  className,
  'data-testid': testId,
}) => (
  <span
    aria-hidden="true"
    className={className}
    data-testid={testId}
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: 2,
      flexShrink: 0,
      backgroundColor: color,
      ...style,
    }}
  />
);

ColorSwatch.displayName = 'ColorSwatch';

// LegendItem — square swatch + label in a flex row

export interface LegendItemProps {
  color: string;
  label: string;
  /** Swatch size in px. Default: 10 */
  swatchSize?: number;
  /** Font size in px. Default: 11 */
  fontSize?: number;
  /** Gap between swatch and label in px. Default: 6 */
  gap?: number;
  /**
   * @internal DS-internal style prop; forbidden in route-owned code.
   */
  style?: React.CSSProperties;
  'data-testid'?: string;
}

/**
 * A horizontal flex row with a square color swatch followed by a text label.
 * Suitable for chart legends, map legends, and form-row identity labels.
 */
export const LegendItem: React.FC<LegendItemProps> = ({
  color,
  label,
  swatchSize = 10,
  fontSize = 11,
  gap = 6,
  style,
  'data-testid': testId,
}) => (
  <div
    data-testid={testId}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap,
      ...style,
    }}
  >
    <ColorSwatch color={color} size={swatchSize} />
    <span style={{ fontSize, color: 'inherit' }}>{label}</span>
  </div>
);

LegendItem.displayName = 'LegendItem';
